export interface AddressFeature {
  geometry: {
    coordinates: [number, number];
  };
  properties: {
    label: string;
    name: string;
    housenumber?: string;
    street?: string;
    postcode?: string;
    city?: string;
    context?: string;
    [key: string]: any;
  };
}

export interface ParsedAddress {
  address: string;
  city: string;
  zip: string;
  latitude: number | null;
  longitude: number | null;
}

function parseCoordinates(coordinates: [number, number]): { lat: number; lng: number } | null {
  const [lngRaw, latRaw] = coordinates;
  const lng = typeof lngRaw === 'number' ? lngRaw : parseFloat(String(lngRaw));
  const lat = typeof latRaw === 'number' ? latRaw : parseFloat(String(latRaw));

  if (isFinite(lat) && isFinite(lng)) {
    return { lat, lng };
  }
  return null;
}

function extractZipFromLabel(label: string): string {
  const zipMatch = label.match(/\b(\d{5})\b/);
  return zipMatch ? zipMatch[1] : '';
}

function extractCityFromLabel(label: string): string {
  const parts = label.split(',').map(p => p.trim());
  if (parts.length > 1) {
    const lastPart = parts[parts.length - 1];
    return lastPart.replace(/\b\d{5}\b/, '').trim();
  }
  return '';
}

export function parseAddressFeature(feature: AddressFeature): ParsedAddress {
  const props = feature.properties;
  const coords = parseCoordinates(feature.geometry.coordinates);

  let city = props.city || '';
  let zip = props.postcode || '';
  const address = props.name || props.label || '';
  const label = props.label || '';

  if (!zip && label) {
    zip = extractZipFromLabel(label);
  }
  if (!city && label) {
    city = extractCityFromLabel(label);
  }

  return {
    address,
    city,
    zip,
    latitude: coords?.lat ?? null,
    longitude: coords?.lng ?? null,
  };
}

export async function reverseGeocode(
  lat: number,
  lng: number
): Promise<{ city?: string; zip?: string }> {
  try {
    const response = await fetch(
      `https://api-adresse.data.gouv.fr/reverse/?lon=${lng}&lat=${lat}`
    );
    const data = await response.json();

    if (data.features && data.features.length > 0) {
      const props = data.features[0].properties;
      return {
        city: props.city,
        zip: props.postcode,
      };
    }
  } catch (error) {
    console.error('Erreur lors de la géocodification inverse:', error);
  }

  return {};
}

export async function parseAddressFeatureComplete(
  feature: AddressFeature
): Promise<ParsedAddress> {
  const parsed = parseAddressFeature(feature);

  if (parsed.latitude && parsed.longitude && (!parsed.city || !parsed.zip)) {
    const reverseData = await reverseGeocode(parsed.latitude, parsed.longitude);
    return {
      ...parsed,
      city: reverseData.city || parsed.city,
      zip: reverseData.zip || parsed.zip,
    };
  }

  return parsed;
}

